import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAngularServices } from '@/react/components'
import { User } from '@/react/types/observations'

interface UseUsersListProps {
  search: string
  order: string
  currentUserId: number
}

interface UseUsersListReturn {
  users: User[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  loadMoreUsers: () => void
  filteredUsers: User[]
}

export const useUsersList = ({
  search,
  order,
  currentUserId,
}: UseUsersListProps): UseUsersListReturn => {
  const { Api } = useAngularServices()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)

  // Memoize the API parameters to prevent unnecessary re-renders
  const apiParams = useMemo(
    () => ({
      page: 1,
      page_size: 10,
      deleted: false,
      is_active: true,
      order: order || 'first_name,last_name',
      ...(search && { search }),
    }),
    [search, order],
  )

  // Memoize the filtered users list to exclude current user
  const filteredUsers = useMemo(() => {
    return users.filter((user) => user.id !== currentUserId)
  }, [users, currentUserId])

  const fetchUsers = useCallback(
    async (isLoadMore = false, pageNum = 1) => {
      if (isLoadMore) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        setUsers([])
      }

      try {
        const params = {
          ...apiParams,
          page: pageNum as number,
        }

        const result = await Api.get('users', params)

        // Type guard to safely access the response
        if (result && typeof result === 'object' && 'data' in result) {
          const data = result.data as { results: User[]; count: number }
          const newUsers = data.results
          const totalCount = data.count

          if (isLoadMore) {
            setUsers((prev) => [...prev, ...newUsers])
          } else {
            setUsers(newUsers)
          }

          const currentTotal = isLoadMore
            ? users.length + newUsers.length
            : newUsers.length
          setHasMore(currentTotal < totalCount)
        }
      } catch (error) {
        console.error('Error fetching users:', error)
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [Api, apiParams, users.length],
  )

  const loadMoreUsers = useCallback(() => {
    if (loadingMore || !hasMore) return

    const nextPage = Math.floor(users.length / 10) + 1
    setPage(nextPage)
    void fetchUsers(true, nextPage)
  }, [loadingMore, hasMore, users.length, fetchUsers])

  // Memoize the effect dependencies to prevent unnecessary API calls
  const effectDependencies = useMemo(
    () => [apiParams.search, apiParams.order],
    [apiParams.search, apiParams.order],
  )

  useEffect(() => {
    void fetchUsers(false, 1)
    setPage(1)
  }, effectDependencies)

  return {
    users,
    loading,
    loadingMore,
    hasMore,
    loadMoreUsers,
    filteredUsers,
  }
}
